// src/components/LineRow.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Stack,
  IconButton,
  Divider,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { IText } from "fabric";

import { useData } from "../context/DataContext";
import { useCanvas } from "../context/CanvasContext";
import { useProductInputs } from "../hooks/useProductInputs";

// FIXED IMPORTS — these are the correct paths
import { getColorsByProductCode } from "../constants/colors";
import { getFinishByProductCode } from "../constants/finishes";
import { getFontsByProductCode } from "../constants/fonts";
import { getHardwareByProductCode } from "../constants/hardwares";
import { getThicknessByProductCode } from "../constants/thicknesses";

import { setDimensions } from "../utils/dimensions";

const MAX_CHARS = 30;

export default function LineRow({ lineId }) {
  const { lines, updateLine, removeLine, selectedProduct, addLine } = useData();

  const { canvas } = useCanvas();

  const line = lines.find((l) => l.id === lineId);
  const lineIndex = lines.findIndex((l) => l.id === lineId);
  const isLastLine = lineIndex === lines.length - 1;
  const canAddMore = lines.length < 5;

  const { inputs } = useProductInputs(selectedProduct?.code);
  const [error, setError] = useState("");

  // ──────────────────────────────────────────────────────────────
  // ALL HOOKS FIRST — NO EARLY RETURNS BEFORE THIS POINT
  // ──────────────────────────────────────────────────────────────

  const finishOptionsAll = getFinishByProductCode(selectedProduct?.code) || [];
  const naturalSatinFinish = finishOptionsAll.find(
    (f) =>
      f.name.toLowerCase().includes("natural satin") ||
      f.code === "FCAL_NSA" ||
      f.code === "Natural_Satin_Aluminum"
  );
  const naturalSatinCode = naturalSatinFinish?.code;

  const colorOptions = getColorsByProductCode(selectedProduct?.code) || [];
  const hardwareOptions = getHardwareByProductCode(selectedProduct?.code) || [];
  const thicknessOptions =
    getThicknessByProductCode(selectedProduct?.code) || [];

  const sizeName =
    selectedProduct?.sizes?.find((s) => s.code === line?.size)?.name ||
    line?.size ||
    "—";

  const hardwareName = line?.hardware
    ? hardwareOptions.find((h) => h.code === line.hardware)?.name || "—"
    : "—";
  const thicknessName = line?.thickness
    ? thicknessOptions.find((t) => t.code === line.thickness)?.name || "—"
    : "—";
  const finishName = line?.finish
    ? finishOptionsAll.find((f) => f.code === line.finish)?.name || "—"
    : "—";

  const displayedColorValue =
    line?.finish && line.finish !== naturalSatinCode
      ? "Unpainted"
      : line?.color || "";

  const charCount = line?.text?.trim().length || 0;
  const formattedPrice =
    line?.price > 0 ? `$${Number(line.price).toFixed(2)}` : "";

  // ──────────────────────────────────────────────────────────────
  // CANVAS SYNC + BUSINESS LOGIC
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvas || !line || !line.canvasObjId || !selectedProduct?.code) return;

    const colorOptions = getColorsByProductCode(selectedProduct.code) || [];
    const finishOptions = getFinishByProductCode(selectedProduct.code) || [];

    // Find Natural Satin ONLY if finishes exist
    const naturalSatinFinish =
      finishOptions.length > 0
        ? finishOptions.find(
            (f) =>
              f.name.toLowerCase().includes("natural satin") ||
              f.code === "FCAL_NSA" ||
              f.code === "Natural_Satin_Aluminum"
          )
        : null;

    const naturalSatinCode = naturalSatinFinish?.code;

    const selectedFinish = finishOptions.find((f) => f.code === line.finish);
    const paintedColor = line.color
      ? colorOptions.find(
          (c) => c.code === line.color || c.mivaname === line.color
        )
      : null;

    // ONLY use finish color if:
    // 1. Product has finishes AND
    // 2. A finish is selected AND
    // 3. It's NOT Natural Satin (which allows painted colors)
    const hasFinishes = finishOptions.length > 0;
    const isNonPaintableFinish =
      hasFinishes && line.finish && line.finish !== naturalSatinCode;

    // FINAL COLOR LOGIC — THIS IS PERFECT
    let finalFillColor = "#FFFFFF"; // safe default (white)

    if (isNonPaintableFinish && selectedFinish?.hex) {
      finalFillColor = selectedFinish.hex;
    } else if (paintedColor?.hex) {
      finalFillColor = paintedColor.hex;
    } else if (selectedProduct?.defaultFillHex) {
      finalFillColor = selectedProduct.defaultFillHex;
    }

    console.log("%c[TEXT COLOR]", "color: #00ff00; font-weight: bold;", {
      product: selectedProduct.code,
      lineId,
      text: line.text || "<empty>",
      hasFinishes,
      finish: line.finish,
      paintedColor: paintedColor?.name,
      isNonPaintableFinish,
      finalFillColor,
    });

    let obj = canvas.getObjects().find((o) => o.id === line.canvasObjId);

    if (!obj) {
      obj = new IText(line.text || "Enter text...", {
        id: line.canvasObjId,
        left: 100,
        top: 100 + lineIndex * 120,
        originX: "center",
        originY: "center",
        fontFamily: line.font || "Arial",
        fontSize: (parseInt(line.size) || 6) * 16,
        fill: finalFillColor,
        selectable: true,
        hasControls: true,
        lockUniScaling: true,
        opacity: 1,
      });
      canvas.add(obj);
    }

    obj.set({
      text: line.text || " ",
      fontFamily: line.font || "Arial",
      fontSize: (parseInt(line.size) || 6) * 16,
      fill: finalFillColor,
    });

    canvas.centerObjectH(obj);
    obj.setCoords();
    canvas.requestRenderAll();

    const handler = () => {
      if (obj.text !== line.text) {
        updateLine(lineId, { text: obj.text });
      }
    };
    obj.off("changed", handler);
    obj.on("changed", handler);

    return () => {
      obj.off("changed", handler);
      const stillExists = canvas
        .getObjects()
        .find((o) => o.id === line.canvasObjId);
      if (stillExists) {
        canvas.remove(stillExists);
        canvas.requestRenderAll();
      }
    };
  }, [
    canvas,
    line?.text,
    line?.font,
    line?.size,
    line?.color,
    line?.finish,
    line?.canvasObjId,
    line?.version,
    lineId,
    lineIndex,
    selectedProduct?.code,
    selectedProduct?.defaultFillHex,
  ]);
  // ──────────────────────────────────────────────────────────────
  // DIMENSIONS
  // ──────────────────────────────────────────────────────────────
  const dimensions = useMemo(() => {
    if (!line?.canvasObjId || !line?.text?.trim() || !canvas) return null;
    const obj = canvas.getObjects().find((o) => o.id === line.canvasObjId);
    if (!obj) return null;

    return setDimensions(
      obj,
      line.font,
      line.size,
      getFontsByProductCode(selectedProduct?.code),
      {
        productCode: selectedProduct,
        heightOverride: selectedProduct?.sizes?.find(
          (s) => s.code === line.size
        )?.height,
        scaleX: obj.scaleX || 1,
        precision: "precise",
      }
    );
  }, [
    line?.canvasObjId,
    line?.text,
    line?.font,
    line?.size,
    selectedProduct,
    canvas,
  ]);

  useEffect(() => {
    if (!dimensions) {
      if (line?.widthInches || line?.heightInches) {
        updateLine(lineId, { widthInches: 0, heightInches: 0 });
      }
      return;
    }
    const w = Number(dimensions.widthInches.toFixed(2));
    const h = Number(dimensions.heightInches.toFixed(2));
    if (line?.widthInches !== w || line?.heightInches !== h) {
      updateLine(lineId, { widthInches: w, heightInches: h });
    }
  }, [dimensions, line?.widthInches, line?.heightInches, lineId, updateLine]);

  // ──────────────────────────────────────────────────────────────
  // EARLY RETURN — AFTER ALL HOOKS
  // ──────────────────────────────────────────────────────────────
  if (!line) return null;

  const handleDelete = () => {
    if (line.canvasObjId) {
      const obj = canvas?.getObjects().find((o) => o.id === line.canvasObjId);
      if (obj) canvas.remove(obj);
    }
    removeLine(lineId);
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid #eee",
        mb: 1.5,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        {inputs.map((input) => {
          const inputKey = `${lineId}-${input.key}`;

          if (input.type === "text") {
            return (
              <TextField
                key={inputKey}
                size="small"
                label={`${input.label} (${charCount}/${MAX_CHARS})`}
                value={line.text || ""}
                onChange={(e) => {
                  if (e.target.value.length > MAX_CHARS) {
                    setError(`Max ${MAX_CHARS} characters`);
                    return;
                  }
                  setError("");
                  updateLine(lineId, { text: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    line.text?.trim() &&
                    canAddMore &&
                    isLastLine
                  ) {
                    e.preventDefault();
                    addLine();
                  }
                }}
                placeholder={input.placeholder}
                error={!!error}
                helperText={error}
                inputProps={{ maxLength: MAX_CHARS + 10 }}
                sx={{ flex: "1 1 200px", minWidth: 0 }}
              />
            );
          }

          if (input.key === "color") {
            return (
              <FormControl key={inputKey} size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{input.label}</InputLabel>
                <Select
                  value={displayedColorValue}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    const colorOptions =
                      getColorsByProductCode(selectedProduct?.code) || [];
                    const isUnpaintedSelection =
                      newColor === "Unpainted" ||
                      colorOptions[0]?.code === newColor ||
                      colorOptions[0]?.mivaname === newColor;

                    const updates = {
                      color:
                        isUnpaintedSelection || line.finish !== naturalSatinCode
                          ? null
                          : newColor,
                      version: (line.version || 0) + 1,
                    };

                    if (
                      !isUnpaintedSelection &&
                      line.finish !== naturalSatinCode
                    ) {
                      updates.finish = naturalSatinCode;
                    }

                    updateLine(lineId, updates);
                  }}
                  label={input.label}
                >
                  {input.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.color ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: opt.color,
                              borderRadius: "50%",
                              border: "1px solid #ccc",
                            }}
                          />
                          {opt.label}
                        </Box>
                      ) : (
                        opt.label
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          if (input.key === "finish") {
            return (
              <FormControl key={inputKey} size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{input.label}</InputLabel>
                <Select
                  value={line.finish || ""}
                  onChange={(e) => {
                    updateLine(lineId, {
                      finish: e.target.value,
                      version: (line.version || 0) + 1,
                    });
                  }}
                  label={input.label}
                >
                  {input.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          return (
            <FormControl key={inputKey} size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{input.label}</InputLabel>
              <Select
                value={line[input.key] || ""}
                onChange={(e) =>
                  updateLine(lineId, { [input.key]: e.target.value })
                }
                label={input.label}
              >
                {input.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {input.key === "font" ? (
                      <Box
                        component="span"
                        sx={{ fontFamily: opt.code, fontSize: "1.1rem" }}
                      >
                        {opt.name}
                      </Box>
                    ) : (
                      opt.label
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{ fontSize: "0.875rem" }}
          >
            {[
              sizeName,
              displayedColorValue !== "Unpainted" ? displayedColorValue : null,
              line.font,
              hardwareName !== "—" ? hardwareName : null,
              finishName !== "—" ? finishName : null,
              thicknessName !== "—" ? thicknessName : null,
            ]
              .filter(Boolean)
              .join(" • ")}
          </Typography>
          {dimensions && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({dimensions.widthInches.toFixed(2)}" ×{" "}
              {dimensions.heightInches.toFixed(2)}")
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {charCount > 0 && (
            <Typography
              variant="body2"
              sx={{ color: "success.main", fontWeight: "bold" }}
            >
              {formattedPrice}
            </Typography>
          )}
          <IconButton size="small" onClick={handleDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
          {isLastLine && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={addLine}
              disabled={!canAddMore}
            >
              {canAddMore ? "Add Line" : "Max 5"}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
